<template>
  <div class="modal fade" id="serviceImportModal" tabindex="-1" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-upload me-2"></i>
            Import Services from CSV
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetImport"></button>
        </div>

        <div class="modal-body">
          <!-- Step indicator -->
          <div class="row mb-4">
            <div class="col">
              <div class="step-indicator">
                <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                  <div class="step-icon">1</div>
                  <div class="step-title">Upload File</div>
                </div>
                <div class="step-line" :class="{ active: currentStep > 1 }"></div>
                <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                  <div class="step-icon">2</div>
                  <div class="step-title">Map Columns</div>
                </div>
                <div class="step-line" :class="{ active: currentStep > 2 }"></div>
                <div class="step" :class="{ active: currentStep >= 3 }">
                  <div class="step-icon">3</div>
                  <div class="step-title">Review & Import</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1: Upload File -->
          <div v-if="currentStep === 1">
            <div class="alert alert-info">
              <i class="bx bx-info-circle me-2"></i>
              Upload a CSV file containing service information. The file should include columns for service code, name, category, and pricing.
            </div>

            <div class="mb-3">
              <label class="form-label">CSV File</label>
              <input
                  type="file"
                  class="form-control"
                  accept=".csv"
                  @change="handleFileUpload"
                  ref="fileInput">
              <small class="form-text text-muted">Maximum file size: 5MB</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Import Options</label>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="importOptions.updateExisting">
                <label class="form-check-label">
                  Update existing services with matching codes
                </label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="importOptions.skipDuplicates">
                <label class="form-check-label">
                  Skip duplicate service codes
                </label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="importOptions.validateCodes">
                <label class="form-check-label">
                  Validate medical codes (ICD, CPT)
                </label>
              </div>
            </div>

            <div class="card bg-light">
              <div class="card-body">
                <h6 class="card-title">Download Sample Template</h6>
                <p class="card-text">Download a sample CSV template with the correct format for importing services.</p>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="downloadTemplate">
                  <i class="bx bx-download me-1"></i>Download Template
                </button>
              </div>
            </div>
          </div>

          <!-- Step 2: Map Columns -->
          <div v-if="currentStep === 2">
            <div class="alert alert-info">
              <i class="bx bx-info-circle me-2"></i>
              Map your CSV columns to the corresponding service fields. Required fields are marked with *.
            </div>

            <div class="table-responsive">
              <table class="table table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Service Field</th>
                  <th>CSV Column</th>
                  <th>Sample Data</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="field in mappingFields" :key="field.key">
                  <td>
                    {{ field.label }}
                    <span v-if="field.required" class="text-danger">*</span>
                  </td>
                  <td>
                    <select
                        class="form-select form-select-sm"
                        v-model="columnMapping[field.key]"
                        :required="field.required">
                      <option value="">-- Not Mapped --</option>
                      <option v-for="(column, index) in csvHeaders" :key="index" :value="index">
                        {{ column }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <small class="text-muted">
                      {{ getSampleData(columnMapping[field.key]) }}
                    </small>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="alert alert-warning" v-if="!isValidMapping">
              <i class="bx bx-error me-2"></i>
              Please map all required fields before proceeding.
            </div>
          </div>

          <!-- Step 3: Review & Import -->
          <div v-if="currentStep === 3">
            <div class="alert alert-info">
              <i class="bx bx-info-circle me-2"></i>
              Review the services to be imported. Fix any errors before importing.
            </div>

            <!-- Summary -->
            <div class="row mb-3">
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <h5 class="text-primary">{{ parsedData.length }}</h5>
                    <small class="text-muted">Total Services</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <h5 class="text-success">{{ validServices.length }}</h5>
                    <small class="text-muted">Valid</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <h5 class="text-warning">{{ duplicateServices.length }}</h5>
                    <small class="text-muted">Duplicates</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <h5 class="text-danger">{{ invalidServices.length }}</h5>
                    <small class="text-muted">Errors</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preview Table -->
            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-striped">
                <thead class="table-light sticky-top">
                <tr>
                  <th>Status</th>
                  <th>Service Code</th>
                  <th>Service Name</th>
                  <th>Category</th>
                  <th>Unit Price</th>
                  <th>ICD Code</th>
                  <th>CPT Code</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(service, index) in previewData" :key="index">
                  <td>
                      <span v-if="service.status === 'valid'" class="badge bg-success">
                        <i class="bx bx-check"></i>
                      </span>
                    <span v-else-if="service.status === 'duplicate'" class="badge bg-warning">
                        <i class="bx bx-copy"></i>
                      </span>
                    <span v-else class="badge bg-danger">
                        <i class="bx bx-x"></i>
                      </span>
                  </td>
                  <td>{{ service.service_code }}</td>
                  <td>{{ service.service_name }}</td>
                  <td>{{ service.service_category }}</td>
                  <td>{{ service.unit_price }}</td>
                  <td>{{ service.icd_code }}</td>
                  <td>{{ service.cpt_code }}</td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Error Details -->
            <div v-if="invalidServices.length > 0" class="alert alert-danger mt-3">
              <h6>Validation Errors:</h6>
              <ul class="mb-0">
                <li v-for="(error, index) in validationErrors.slice(0, 5)" :key="index">
                  Row {{ error.row }}: {{ error.message }}
                </li>
                <li v-if="validationErrors.length > 5">
                  ... and {{ validationErrors.length - 5 }} more errors
                </li>
              </ul>
            </div>
          </div>

          <!-- Progress Bar -->
          <div v-if="importing" class="mt-3">
            <div class="progress">
              <div class="progress-bar progress-bar-striped progress-bar-animated"
                   :style="`width: ${importProgress}%`">
                {{ importProgress }}%
              </div>
            </div>
            <p class="text-center mt-2">{{ importMessage }}</p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="previousStep" v-if="currentStep > 1 && !importing">
            <i class="bx bx-chevron-left me-1"></i>Previous
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetImport" v-if="!importing">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="nextStep"
                  v-if="currentStep < 3 && canProceed" :disabled="!canProceed">
            Next<i class="bx bx-chevron-right ms-1"></i>
          </button>
          <button type="button" class="btn btn-success" @click="importServices"
                  v-if="currentStep === 3 && !importing" :disabled="validServices.length === 0">
            <i class="bx bx-upload me-1"></i>Import {{ validServices.length }} Services
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ServiceImportModal",

  data() {
    return {
      currentStep: 1,
      file: null,
      csvHeaders: [],
      csvData: [],
      parsedData: [],
      previewData: [],
      columnMapping: {
        service_code: '',
        service_name: '',
        service_category: '',
        unit_price: '',
        icd_code: '',
        cpt_code: '',
        snomed_code: '',
        local_code: '',
        description: ''
      },
      mappingFields: [
        { key: 'service_code', label: 'Service Code', required: true },
        { key: 'service_name', label: 'Service Name', required: true },
        { key: 'service_category', label: 'Category', required: true },
        { key: 'unit_price', label: 'Unit Price', required: true },
        { key: 'icd_code', label: 'ICD Code', required: false },
        { key: 'cpt_code', label: 'CPT Code', required: false },
        { key: 'snomed_code', label: 'SNOMED Code', required: false },
        { key: 'local_code', label: 'Local Code', required: false },
        { key: 'description', label: 'Description', required: false }
      ],
      importOptions: {
        updateExisting: false,
        skipDuplicates: true,
        validateCodes: false
      },
      validServices: [],
      invalidServices: [],
      duplicateServices: [],
      validationErrors: [],
      importing: false,
      importProgress: 0,
      importMessage: '',
      modal: null
    };
  },

  computed: {
    canProceed() {
      if (this.currentStep === 1) {
        return this.file !== null;
      } else if (this.currentStep === 2) {
        return this.isValidMapping;
      }
      return false;
    },

    isValidMapping() {
      // Check if all required fields are mapped
      return this.mappingFields
          .filter(field => field.required)
          .every(field => this.columnMapping[field.key] !== '');
    }
  },

  mounted() {
    this.modal = new bootstrap.Modal(document.getElementById('serviceImportModal'));
  },

  methods: {
    openModal() {
      this.resetImport();
      this.modal.show();
    },

    closeModal() {
      this.modal.hide();
      this.resetImport();
    },

    resetImport() {
      this.currentStep = 1;
      this.file = null;
      this.csvHeaders = [];
      this.csvData = [];
      this.parsedData = [];
      this.previewData = [];
      this.columnMapping = {
        service_code: '',
        service_name: '',
        service_category: '',
        unit_price: '',
        icd_code: '',
        cpt_code: '',
        snomed_code: '',
        local_code: '',
        description: ''
      };
      this.validServices = [];
      this.invalidServices = [];
      this.duplicateServices = [];
      this.validationErrors = [];
      this.importing = false;
      this.importProgress = 0;
      this.importMessage = '';

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit');
        event.target.value = '';
        return;
      }

      this.file = file;
      this.parseCSV(file);
    },

    parseCSV(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('CSV file appears to be empty or invalid');
          return;
        }

        // Parse headers
        this.csvHeaders = this.parseCSVLine(lines[0]);

        // Parse data
        this.csvData = [];
        for (let i = 1; i < lines.length; i++) {
          const values = this.parseCSVLine(lines[i]);
          if (values.length === this.csvHeaders.length) {
            this.csvData.push(values);
          }
        }

        // Auto-map columns based on header names
        this.autoMapColumns();
      };
      reader.readAsText(file);
    },

    parseCSVLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }

      result.push(current.trim());
      return result;
    },

    autoMapColumns() {
      // Try to automatically map columns based on header names
      const mappings = {
        'service_code': ['service_code', 'code', 'service code', 'servicecode'],
        'service_name': ['service_name', 'name', 'service name', 'servicename', 'description'],
        'service_category': ['service_category', 'category', 'service category', 'type'],
        'unit_price': ['unit_price', 'price', 'unit price', 'cost', 'amount'],
        'icd_code': ['icd_code', 'icd', 'icd code', 'icd-10', 'icd10'],
        'cpt_code': ['cpt_code', 'cpt', 'cpt code'],
        'snomed_code': ['snomed_code', 'snomed', 'snomed code'],
        'local_code': ['local_code', 'local', 'local code'],
        'description': ['description', 'desc', 'details', 'notes']
      };

      for (const [field, variations] of Object.entries(mappings)) {
        for (let i = 0; i < this.csvHeaders.length; i++) {
          const header = this.csvHeaders[i].toLowerCase();
          if (variations.includes(header)) {
            this.columnMapping[field] = i;
            break;
          }
        }
      }
    },

    getSampleData(columnIndex) {
      if (columnIndex === '' || !this.csvData.length) return '';
      return this.csvData[0][columnIndex] || '';
    },

    nextStep() {
      if (this.currentStep === 2) {
        this.preparePreview();
      }
      this.currentStep++;
    },

    previousStep() {
      this.currentStep--;
    },

    preparePreview() {
      this.parsedData = [];
      this.validServices = [];
      this.invalidServices = [];
      this.duplicateServices = [];
      this.validationErrors = [];

      for (let i = 0; i < this.csvData.length; i++) {
        const row = this.csvData[i];
        const service = {};

        // Map data according to column mapping
        for (const [field, columnIndex] of Object.entries(this.columnMapping)) {
          if (columnIndex !== '') {
            service[field] = row[columnIndex] || '';
          }
        }

        // Validate the service
        const validation = this.validateService(service, i + 2); // +2 for row number (header + 0-index)
        service.status = validation.status;
        service.errors = validation.errors;

        this.parsedData.push(service);

        if (validation.status === 'valid') {
          this.validServices.push(service);
        } else if (validation.status === 'duplicate') {
          this.duplicateServices.push(service);
        } else {
          this.invalidServices.push(service);
          if (validation.errors.length > 0) {
            this.validationErrors.push({
              row: i + 2,
              message: validation.errors.join(', ')
            });
          }
        }
      }

      // Prepare preview data (show first 100 rows)
      this.previewData = this.parsedData.slice(0, 100);
    },

    validateService(service, rowNumber) {
      const errors = [];
      let status = 'valid';

      // Check required fields
      if (!service.service_code) {
        errors.push('Missing service code');
        status = 'invalid';
      }
      if (!service.service_name) {
        errors.push('Missing service name');
        status = 'invalid';
      }
      if (!service.service_category) {
        errors.push('Missing category');
        status = 'invalid';
      }
      if (!service.unit_price || isNaN(service.unit_price)) {
        errors.push('Invalid unit price');
        status = 'invalid';
      }

      // Check for duplicates (within the CSV)
      if (status === 'valid') {
        const duplicates = this.parsedData.filter(s =>
            s.service_code === service.service_code
        );
        if (duplicates.length > 0) {
          status = 'duplicate';
        }
      }

      return { status, errors };
    },

    async importServices() {
      this.importing = true;
      this.importProgress = 0;
      this.importMessage = 'Preparing import...';

      const data = {
        "function": "bulkImportServices",
        "services": this.validServices,
        "options": this.importOptions
      };

      try {
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          if (this.importProgress < 90) {
            this.importProgress += 10;
            this.importMessage = `Importing services... ${this.importProgress}%`;
          }
        }, 500);

        const res = await executeJson(data, authUrl);

        clearInterval(progressInterval);
        this.importProgress = 100;

        if (res.data.success) {
          this.importMessage = 'Import completed successfully!';

          setTimeout(() => {
            this.$emit('import-complete', {
              message: `Successfully imported ${res.data.data.imported} services`,
              imported: res.data.data.imported,
              updated: res.data.data.updated || 0,
              skipped: res.data.data.skipped || 0
            });
            this.closeModal();
          }, 1000);
        } else {
          this.importMessage = 'Import failed!';
          this.$emit('import-error', {
            message: res.data.message || 'Failed to import services'
          });
        }
      } catch (e) {
        this.importMessage = 'Import error!';
        this.$emit('import-error', {
          message: e.message || 'An error occurred during import'
        });
      } finally {
        setTimeout(() => {
          this.importing = false;
        }, 2000);
      }
    },

    downloadTemplate() {
      // Create CSV template
      const headers = [
        'service_code',
        'service_name',
        'service_category',
        'unit_price',
        'description',
        'icd_code',
        'cpt_code',
        'snomed_code',
        'local_code'
      ];

      const sampleData = [
        ['CONS-001', 'General Consultation', 'Consultation', '150.00', 'General practitioner consultation', 'Z00.00', '99213', '', ''],
        ['LAB-001', 'Complete Blood Count', 'Laboratory', '120.00', 'Full blood count test', 'Z01.812', '85025', '', ''],
        ['RAD-001', 'Chest X-Ray', 'Radiology', '200.00', 'Standard chest X-ray', 'Z01.88', '71020', '', '']
      ];

      let csv = headers.join(',') + '\n';
      sampleData.forEach(row => {
        csv += row.map(cell => `"${cell}"`).join(',') + '\n';
      });

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'services_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.step.active .step-icon {
  background-color: #0d6efd;
  color: white;
}

.step.completed .step-icon {
  background-color: #28a745;
  color: white;
}

.step-title {
  font-size: 0.875rem;
  color: #6c757d;
}

.step.active .step-title {
  color: #0d6efd;
  font-weight: 600;
}

.step-line {
  width: 100px;
  height: 2px;
  background-color: #e9ecef;
  margin: 0 1rem;
  margin-bottom: 2rem;
}

.step-line.active {
  background-color: #28a745;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}
</style>